import { CallFullSizeDto, Role, UserFullSizeDto } from '@therame/shared';
import dayjs from 'dayjs';
import { onBeforeUnmount, ref } from 'vue';

export function str_pad_left(number: number, pad: string, length: number) {
  return (Array.from({ length: length + 1 }).join(pad) + number).slice(-length);
}

export const formatSecondsToMinutes = (seconds: number) => {
  return dayjs().set('minute', 0).set('seconds', 0).add(seconds, 'seconds').format('mm:ss');
};

export function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}
export function describeArc(
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(
    ' ',
  );
  return d;
}

export const useCurrentTime = () => {
  const currentTime = ref(new Date());
  const updateCurrentTime = () => {
    currentTime.value = new Date();
  };
  const updateTimeInterval = setInterval(updateCurrentTime, 1000);
  onBeforeUnmount(() => {
    clearInterval(updateTimeInterval);
  });
  return {
    currentTime,
  };
};

export function getTimeRemaining(refTime: Date) {
  const time = dayjs(refTime).diff(dayjs()) / 1000 / 60;
  const hours = Math.floor(time / 60);
  const minutes = Math.floor(time % 60);

  return { h: hours, m: minutes, t: Math.round(time) };
}

export function emailValid(email: string | null) {
  if (email === null || email === undefined || email.length === 0 || typeof email !== 'string') {
    return false;
  }
  const res = String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  return res !== null && res.length > 0;
}

export function getGenderFromSalutation(salutation: string) {
  switch (salutation) {
    case 'Frau':
      return 'Weiblich';
    case 'Herr':
      return 'Männlich';
    default:
      return 'Divers';
  }
}

export function getHalfInitials(user: UserFullSizeDto) {
  return user?.firstName && user?.lastName
    ? user?.firstName[0].toLocaleUpperCase() + '. ' + user?.lastName
    : '-';
}

export function getInitials(user: { firstName?: string | null; lastName?: string | null } | null) {
  return user?.firstName && user?.lastName
    ? user?.firstName[0].toLocaleUpperCase() + user?.lastName[0].toLocaleUpperCase()
    : '-';
}

export function getRoleNameFromRoleEnum(role: Role): string {
  switch (role) {
    case Role.SUPERADMIN:
      return 'Super-Admin';
    case Role.ADMIN:
      return 'Administrator:in';
    case Role.THERAPIST:
      return 'Therapeut:in';
    case Role.MANAGEMENT:
      return 'Verwaltung';
    case Role.PATIENT:
      return 'Patient:in';
    case Role.PREVENTION:
      return 'Nutzer:in';
    default:
      return '-';
  }
}

export const getLevelFromStream = async (stream: MediaStream) => {
  const audioContext = new AudioContext();
  const mediaStreamAudioSourceNode = audioContext.createMediaStreamSource(stream);
  const analyserNode = audioContext.createAnalyser();
  mediaStreamAudioSourceNode.connect(analyserNode);

  const pcmData = new Float32Array(analyserNode.fftSize);

  analyserNode.getFloatTimeDomainData(pcmData);
  let sumSquares = 0.0;
  for (const amplitude of pcmData) {
    sumSquares += amplitude * amplitude;
  }
  return Math.sqrt(sumSquares / pcmData.length);
};

export function getCallStatus(call: CallFullSizeDto) {
  if (call.isCanceled || call.isDeclined) {
    return 'Abgesagt';
  }

  return call.isInitialized ? 'Absolviert' : 'Offen';
}

export function callIsPast(call: { scheduledStart: Date; plannedDuration: number }) {
  return dayjs(call.scheduledStart).add(call.plannedDuration, 'minutes').isBefore(dayjs());
}

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const binary = [];
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary.push(String.fromCharCode(bytes[i]));
  }
  return window.btoa(binary.join(''));
};

const getBase64 = (file: File, callback: (base64: string) => void) => {
  const fileReader = new FileReader();
  fileReader.onloadend = function () {
    const arrayBuffer = fileReader.result as ArrayBuffer;
    const base64img = `data:${file.type};base64,${arrayBufferToBase64(arrayBuffer)}`;
    callback(base64img);
  };
  fileReader.readAsArrayBuffer(file);
};

export const redrawImage = async (
  file: File,
  callback: (formData: FormData) => void,
): Promise<boolean> => {
  return new Promise((resolve) => {
    getBase64(file, async (base64) => {
      const img = new Image();
      img.src = base64;

      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        canvas.toBlob(async (blob) => {
          if (!blob) {
            resolve(false);
            return;
          }

          const formData = new FormData();
          formData.append('file', blob, file.name);

          callback(formData);
          resolve(true);
        }, file.type);
      };

      img.onerror = () => {
        resolve(false);
      };
    });
  });
};

export const getFormattedTimeBySeconds = (totalSeconds: number) => {
  const totalHours = Math.floor(totalSeconds / (60 * 60));
  const remainingSeconds1 = totalSeconds - totalHours * 60 * 60;
  const totalMinutes = Math.floor(remainingSeconds1 / 60);
  const remainingSeconds2 = totalSeconds - totalMinutes * 60;

  const hourTime = totalHours > 0 ? totalHours.toString().padStart(2, '0') + ':' : '';
  const minuteTime =
    Math.floor(totalMinutes).toString().padStart(2, '0') +
    ':' +
    Math.round(remainingSeconds2 % 60)
      .toString()
      .padStart(2, '0');

  return hourTime + minuteTime + (totalHours > 0 ? ' h' : ' min');
};
